/**
 * Public OBS browser-source page (issue #9 / StreamView). No auth — an OBS
 * Browser Source can't carry a session cookie, so this is reached purely by
 * the opaque token in the URL (see api/src/handlers/overlay.ts). Renders
 * transparent so OBS composites it directly over game/webcam capture.
 *
 * A 404 (bad/regenerated token) renders nothing rather than an error card —
 * this gets broadcast live, so silence beats a visible "not found" box.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError, type OverlayReadResponse } from "../lib/api";
import { config } from "../lib/config";
import { OverlayCanvas, type OverlayCanvasHandle } from "../components/OverlayCanvas";

const RECONNECT_MS = 2000;

export function Overlay() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<OverlayReadResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const canvasRef = useRef<OverlayCanvasHandle>(null);

  useEffect(() => {
    document.body.style.background = "transparent";
    return () => {
      document.body.style.background = "";
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const refresh = async () => {
      try {
        const res = await api.overlay(token);
        if (!cancelled) setData(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
        else console.error("overlay refresh failed:", err);
      }
    };
    void refresh();

    let retry: ReturnType<typeof setTimeout> | undefined;
    let socket: WebSocket | null = null;
    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(`${config.wsUrl}?overlayToken=${encodeURIComponent(token)}`);
      socket = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as {
            type?: string;
            action?: "play" | "pause";
          };
          if (msg.type === "queue.updated") void refresh();
          else if (msg.type === "overlay.control" && msg.action) {
            canvasRef.current?.sendPlaybackCommand(msg.action);
          }
        } catch {
          /* ignore non-JSON frames */
        }
      };
      ws.onclose = () => {
        if (!cancelled) retry = setTimeout(connect, RECONNECT_MS);
      };
      ws.onerror = () => ws.close();
    };
    connect();

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      socket?.close();
    };
  }, [token]);

  if (!token || notFound || !data) return null;

  return (
    <OverlayCanvas
      ref={canvasRef}
      layout={data.layout}
      font={data.font}
      fontSize={data.fontSize}
      nowPlayingWidth={data.nowPlayingWidth}
      nowPlaying={data.nowPlaying}
      mode="render"
      muted={data.muted}
      autoplay={data.autoplay}
      videoOpacity={data.videoOpacity}
      videoWidth={data.videoWidth}
    />
  );
}
